import { useEffect, useState, useMemo, useRef } from 'react';
import { supabase } from '@/services/supabase';
import { Link } from 'react-router-dom';
import { FileText, Loader2, RefreshCw, ChevronRight, Search, Plus, PauseCircle, PlayCircle, ShieldAlert, CalendarDays } from 'lucide-react';
import { fetchAllInvoices, fetchAccounts, updateAccountFeatures, type InvoiceWithAccount } from '@/services/accounts';
import { createManualInvoice, runSuspensionCheck } from '@/services/billing';
import { InvoiceFelActions, type AccountFelDefaults } from '@/components/billing/InvoiceFelActions';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import type { Account } from '@asi/shared';

// DD/MM/YYYY display wrapper around the native date picker.
// Chrome ignores the `lang` attribute on date inputs, so we overlay a read-only
// text input (formatted DD/MM/YYYY) and trigger showPicker() on the hidden input.
function DatePickerDDMMYYYY({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const nativeRef = useRef<HTMLInputElement>(null);

  const display = value?.length === 10
    ? `${value.slice(8, 10)}/${value.slice(5, 7)}/${value.slice(0, 4)}`
    : '';

  function open() {
    const el = nativeRef.current;
    if (!el) return;
    try { el.showPicker(); } catch { el.focus(); }
  }

  return (
    <div className="relative mt-1">
      <Input
        type="text"
        value={display}
        readOnly
        placeholder="DD/MM/YYYY"
        className="cursor-pointer pr-9"
        onClick={open}
      />
      <CalendarDays
        className="absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none"
      />
      <input
        ref={nativeRef}
        type="date"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="sr-only"
        tabIndex={-1}
      />
    </div>
  );
}

const STATUS_CONFIG: Record<string, { label: string; color: string }> = {
  draft:   { label: 'Borrador', color: 'bg-gray-500/10 text-gray-500' },
  issued:  { label: 'Emitida',  color: 'bg-blue-500/10 text-blue-600' },
  paid:    { label: 'Pagada',   color: 'bg-green-500/10 text-green-600' },
  overdue: { label: 'Vencida',  color: 'bg-red-500/10 text-red-600' },
  void:    { label: 'Anulada',  color: 'bg-gray-500/10 text-gray-400' },
};

const STATUS_TABS = [
  { key: '',        label: 'Todas' },
  { key: 'issued',  label: 'Emitidas' },
  { key: 'paid',    label: 'Pagadas' },
  { key: 'overdue', label: 'Vencidas' },
  { key: 'draft',   label: 'Borradores' },
  { key: 'void',    label: 'Anuladas' },
];

export function InvoicesAdminPage() {
  const [invoices, setInvoices] = useState<InvoiceWithAccount[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState('');
  const [search, setSearch] = useState('');

  // ── Manual invoice creation ───────────────────────────────────────────────
  const [createOpen, setCreateOpen] = useState(false);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [accountQuery, setAccountQuery] = useState('');
  // Default due_date: 30 days from today in YYYY-MM-DD (required by <input type="date">)
  const defaultDueDate = () => {
    const d = new Date();
    d.setDate(d.getDate() + 30);
    return d.toISOString().slice(0, 10);
  };

  const [form, setForm] = useState({
    account_id:  '',
    amount:      '',
    description: '',
    due_date:    defaultDueDate(),
    notes:       '',
    status:      'draft' as 'draft' | 'issued',
  });
  const [submitting, setSubmitting] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [checkingSupension, setCheckingSuspension] = useState(false);

  async function handleSuspensionCheck() {
    setCheckingSuspension(true);
    try {
      await runSuspensionCheck();
      await load({ silent: true });
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setCheckingSuspension(false);
    }
  }

  async function openCreate() {
    setForm({ account_id: '', amount: '', description: '', due_date: defaultDueDate(), notes: '', status: 'draft' });
    setAccountQuery('');
    setCreateOpen(true);
    setCreateError(null);
    if (accounts.length === 0) {
      try {
        const list = await fetchAccounts();
        setAccounts(list);
      } catch (e) {
        setCreateError(e instanceof Error ? e.message : String(e));
      }
    }
  }

  const filteredAccounts = useMemo(() => {
    const q = accountQuery.trim().toLowerCase();
    if (!q) return accounts.slice(0, 30);
    return accounts.filter((a) =>
      a.name?.toLowerCase().includes(q) ||
      a.account_number?.toLowerCase().includes(q) ||
      (a as unknown as { fel_nit?: string | null }).fel_nit?.toLowerCase().includes(q),
    ).slice(0, 30);
  }, [accounts, accountQuery]);

  async function submitCreate() {
    setCreateError(null);
    const amt = Number(form.amount);
    if (!form.account_id) { setCreateError('Selecciona una cuenta.'); return; }
    if (!Number.isFinite(amt) || amt <= 0) { setCreateError('Monto inv\u00e1lido.'); return; }
    if (!form.description.trim()) { setCreateError('La descripci\u00f3n es obligatoria.'); return; }
    setSubmitting(true);
    try {
      await createManualInvoice({
        account_id:  form.account_id,
        amount:      Math.round(amt * 100) / 100,
        description: form.description.trim(),
        due_date:    form.due_date || null,
        notes:       form.notes.trim() || null,
        status:      form.status,
      });
      setCreateOpen(false);
      setForm({ account_id: '', amount: '', description: '', due_date: defaultDueDate(), notes: '', status: 'draft' });
      setAccountQuery('');
      await load();
    } catch (e) {
      setCreateError(e instanceof Error ? e.message : String(e));
    } finally {
      setSubmitting(false);
    }
  }

  async function load(opts?: { silent?: boolean }) {
    if (!opts?.silent) setLoading(true);
    setError(null);
    try {
      const data = await fetchAllInvoices({ limit: 200 });
      setInvoices(data);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      if (!opts?.silent) setLoading(false);
    }
  }

  // Silent refresh used by child actions (FEL emit/cancel) so the page does
  // not blank out into a spinner — the row updates in place.
  const silentReload = () => load({ silent: true });

  // Keep a stable ref to silentReload so the channel callback doesn't go stale.
  const silentReloadRef = useRef(silentReload);
  silentReloadRef.current = silentReload;

  useEffect(() => {
    load();

    const channel = supabase
      .channel('invoices-admin-rt')
      .on('postgres_changes', { event: '*',      schema: 'public', table: 'invoices'  }, () => silentReloadRef.current())
      .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'accounts'  }, () => silentReloadRef.current())
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, []);

  const filtered = useMemo(() => {
    let list = invoices;
    if (statusFilter) list = list.filter((i) => i.status === statusFilter);
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter((i) =>
        i.invoice_number?.toLowerCase().includes(q) ||
        i.account?.name?.toLowerCase().includes(q) ||
        i.account?.account_number?.toLowerCase().includes(q) ||
        i.description?.toLowerCase().includes(q),
      );
    }
    return list;
  }, [invoices, statusFilter, search]);

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-foreground">Facturas</h1>
          <p className="text-sm text-muted-foreground">Todas las facturas del sistema · {invoices.length} en total</p>
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" onClick={openCreate}>
            <Plus className="w-4 h-4 mr-1" />
            Nueva factura
          </Button>
          <Button variant="outline" size="sm" onClick={handleSuspensionCheck} disabled={checkingSupension}>
            {checkingSupension
              ? <Loader2 className="w-4 h-4 mr-1 animate-spin" />
              : <ShieldAlert className="w-4 h-4 mr-1" />}
            Revisar suspensiones
          </Button>
          <Button variant="outline" size="sm" onClick={() => load()} disabled={loading}>
            <RefreshCw className={cn('w-4 h-4 mr-1', loading && 'animate-spin')} />
            Actualizar
          </Button>
        </div>
      </div>

      {/* Search + filter bar */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            className="pl-9"
            placeholder="Buscar por cuenta o número de factura…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div className="flex gap-1 flex-wrap">
          {STATUS_TABS.map((tab) => (
            <button
              key={tab.key}
              type="button"
              onClick={() => setStatusFilter(tab.key)}
              className={cn(
                'px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors',
                statusFilter === tab.key
                  ? 'bg-primary text-primary-foreground border-primary'
                  : 'border-border text-muted-foreground hover:bg-muted',
              )}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {error && (
        <div className="rounded-lg border border-rose-500/30 bg-rose-500/10 px-4 py-3 text-sm text-rose-600">
          {error}
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-16">
          <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
        </div>
      ) : filtered.length === 0 ? (
        <Card>
          <CardContent className="py-16 text-center">
            <FileText className="w-10 h-10 text-muted-foreground mx-auto mb-2 opacity-30" />
            <p className="text-sm text-muted-foreground">Sin facturas{statusFilter || search ? ' con ese filtro' : ''}.</p>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <div className="divide-y divide-border">
            {filtered.map((inv) => {
              const sc = STATUS_CONFIG[inv.status] ?? { label: inv.status, color: '' };
              const acct = inv.account;
              const accountFel: AccountFelDefaults = {
                fel_nit:           acct?.fel_nit           ?? null,
                fel_legal_name:    acct?.fel_legal_name    ?? null,
                fel_address:       acct?.fel_address       ?? null,
                fel_email_invoice: acct?.fel_email_invoice ?? null,
                billing_name:      acct?.billing_name      ?? null,
                billing_email:     acct?.billing_email     ?? null,
                billing_address:   acct?.billing_address   ?? null,
                accountName:       acct?.name              ?? 'Cliente',
              };

              return (
                <div key={inv.id} className="flex items-start gap-4 px-5 py-3.5">
                  <FileText className="w-4 h-4 text-muted-foreground shrink-0 mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="text-sm font-medium text-foreground">{inv.invoice_number}</p>
                      <Badge className={cn('text-xs', sc.color)}>{sc.label}</Badge>
                      {inv.fel_uuid && (
                        <span className="text-[11px] text-emerald-600 dark:text-emerald-400 font-mono">
                          FEL ✓
                        </span>
                      )}
                    </div>
                    {acct && (
                      <Link
                        to={`/accounts/${acct.id}?tab=billing`}
                        className="inline-flex items-center gap-1 text-xs text-primary hover:underline mt-0.5"
                      >
                        {acct.name}
                        {acct.account_number && <span className="text-muted-foreground">#{acct.account_number}</span>}
                        <ChevronRight className="w-3 h-3" />
                      </Link>
                    )}
                    {inv.description && (
                      <p className="text-xs text-muted-foreground mt-0.5 truncate">{inv.description}</p>
                    )}
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {inv.issued_at ? new Date(inv.issued_at).toLocaleDateString('es-GT') : new Date(inv.created_at).toLocaleDateString('es-GT')}
                      {inv.paid_at && ` · Pagada ${new Date(inv.paid_at).toLocaleDateString('es-GT')}`}
                      {inv.payment_method && ` · ${inv.payment_method}`}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <p className="text-sm font-semibold text-foreground">
                      {inv.currency} {inv.amount.toFixed(2)}
                    </p>
                    {/* Suspend/reactivate shortcut for overdue invoices */}
                    {acct && (inv.status === 'overdue' || inv.status === 'issued') && (() => {
                      const suspended = acct.feature_cameras === false && acct.feature_panels === false;
                      return (
                        <button
                          type="button"
                          title={suspended ? 'Reactivar servicios' : 'Suspender por mora'}
                          onClick={async (e) => {
                            e.stopPropagation();
                            await updateAccountFeatures(acct.id, suspended
                              ? { feature_cameras: true, feature_panels: true, feature_patrols: true, feature_arc: true }
                              : { feature_cameras: false, feature_panels: false, feature_patrols: false });
                            await load();
                          }}
                          className={`p-1.5 rounded-lg border transition-colors ${suspended ? 'bg-green-500/10 text-green-600 border-green-300/50 hover:bg-green-500/20' : 'bg-red-500/10 text-red-500 border-red-300/50 hover:bg-red-500/20'}`}
                        >
                          {suspended ? <PlayCircle className="w-3.5 h-3.5" /> : <PauseCircle className="w-3.5 h-3.5" />}
                        </button>
                      );
                    })()}
                    {acct && (
                      <InvoiceFelActions
                        invoice={inv}
                        accountId={acct.id}
                        accountFel={accountFel}
                        onEmitted={silentReload}
                        onVoided={(id) =>
                          setInvoices((prev) =>
                            prev.map((x) => (x.id === id ? { ...x, status: 'void' } : x)),
                          )
                        }
                      />
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </Card>
      )}

      {/* ── Manual invoice creation modal ─────────────────────────────────── */}
      {createOpen && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="bg-card border border-border rounded-2xl w-full max-w-lg p-5 space-y-3 max-h-[90vh] overflow-y-auto">
            <div>
              <h3 className="text-lg font-semibold text-foreground">Nueva factura manual</h3>
              <p className="text-xs text-muted-foreground">Crea una factura puntual fuera del ciclo de cobros recurrentes.</p>
            </div>

            <div>
              <label className="text-xs font-medium text-foreground">Cuenta *</label>
              <Input
                placeholder="Buscar por nombre, número o NIT…"
                value={accountQuery}
                onChange={(e) => setAccountQuery(e.target.value)}
                className="mt-1"
              />
              <div className="mt-1 max-h-48 overflow-y-auto border border-border rounded-lg divide-y divide-border">
                {filteredAccounts.length === 0 ? (
                  <p className="text-xs text-muted-foreground p-3">Sin resultados.</p>
                ) : filteredAccounts.map((a) => (
                  <button
                    key={a.id}
                    type="button"
                    onClick={() => {
                      setForm((f) => ({ ...f, account_id: a.id }));
                      setAccountQuery(a.name ?? '');
                    }}
                    className={cn(
                      'w-full text-left px-3 py-2 text-xs hover:bg-muted',
                      form.account_id === a.id && 'bg-primary/10 text-primary',
                    )}
                  >
                    <div className="font-medium">{a.name}</div>
                    <div className="text-muted-foreground">
                      #{a.account_number}
                      {(a as unknown as { fel_nit?: string | null }).fel_nit && ` · NIT ${(a as unknown as { fel_nit: string }).fel_nit}`}
                    </div>
                  </button>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-medium text-foreground">Monto (GTQ) *</label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={form.amount}
                  onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))}
                  placeholder="0.00"
                  className="mt-1"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-foreground">Fecha vencimiento</label>
                <DatePickerDDMMYYYY
                  value={form.due_date}
                  onChange={(v) => setForm((f) => ({ ...f, due_date: v }))}
                />
                <p className="text-xs text-muted-foreground mt-1">Por defecto: 30 días desde hoy.</p>
              </div>
            </div>

            <div>
              <label className="text-xs font-medium text-foreground">Descripción *</label>
              <Input
                value={form.description}
                onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                placeholder="Ej. Servicio mensual de monitoreo"
                className="mt-1"
              />
            </div>

            <div>
              <label className="text-xs font-medium text-foreground">Notas internas</label>
              <Input
                value={form.notes}
                onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
                placeholder="Opcional"
                className="mt-1"
              />
            </div>

            <div>
              <label className="text-xs font-medium text-foreground">Estado inicial</label>
              <div className="flex gap-2 mt-1">
                {(['draft', 'issued'] as const).map((s) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => setForm((f) => ({ ...f, status: s }))}
                    className={cn(
                      'px-3 py-1.5 rounded-lg text-xs font-medium border',
                      form.status === s ? 'bg-primary text-primary-foreground border-primary' : 'border-border text-muted-foreground',
                    )}
                  >
                    {s === 'draft' ? 'Borrador' : 'Emitida'}
                  </button>
                ))}
              </div>
              <p className="text-[11px] text-muted-foreground mt-1">
                {form.status === 'draft'
                  ? 'Queda como borrador; podrás revisarla y emitirla a FEL después.'
                  : 'Se marca como emitida pero NO se manda a FEL automáticamente — usa el botón "Emitir FEL" en la lista.'}
              </p>
            </div>

            {createError && (
              <div className="rounded-lg border border-rose-500/30 bg-rose-500/10 px-3 py-2 text-xs text-rose-600">
                {createError}
              </div>
            )}

            <div className="flex items-center justify-end gap-2 pt-2">
              <Button variant="ghost" onClick={() => setCreateOpen(false)} disabled={submitting}>Cancelar</Button>
              <Button onClick={submitCreate} disabled={submitting || !form.account_id}>
                {submitting ? <><Loader2 className="w-4 h-4 animate-spin mr-2" />Creando…</> : 'Crear factura'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
